import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FocusScope } from "@radix-ui/react-focus-scope";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Check, Crop, Eye, FlipHorizontal2, Loader2, RotateCw, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { FX_PRESETS, DEFAULT_FX, ASPECTS, normalizeFx, hasFx, fxStyle, fxFilterCss, bakeImageFx, fileFromUrl } from "@/lib/mediaFx";
import { BEAUTY_LEVELS, smoothImageToUrl } from "@/lib/faceSmooth";
import { toast } from "sonner";

// Lot 534 — Éditeur plein écran « Filtres · Réglages · Recadrer » pour une photo ou une vidéo (stories, publications,
// reels). Photo : tout est cuit dans un nouveau fichier (onApply({ file })). Vidéo : filtre + miroir seulement, renvoyés
// en métadonnées (onApply({ fx })) et appliqués en CSS par les lecteurs. Rendu en portail (body) : le composeur PC vit
// dans un Dialog Radix translaté → un `fixed` enfant y resterait piégé ; pointer-events rétablis sous le verrou Radix.
// Lot 536b — Réglages › « Beauté » (photos) : lissage visage MediaPipe 0..3, aperçu lissé (≤ 1600 px) puis cuit à
// l'enregistrement ; le cadre de recadrage se calcule sur les dimensions de l'image de travail, pas de l'aperçu.
const TABS = [
  { key: "filters", Icon: Sparkles, label: ["fx.tab_filters", "Filtres"] },
  { key: "adjust", Icon: SlidersHorizontal, label: ["fx.tab_adjust", "Réglages"] },
  { key: "crop", Icon: Crop, label: ["fx.tab_crop", "Recadrer"] },
];
const GRADIENT_THUMB = "linear-gradient(135deg, #f59e0b 0%, #ef4444 35%, #3b82f6 70%, #10b981 100%)";
const ADJUSTS = [["brightness", "fx.brightness", "Luminosité", 50, 150], ["contrast", "fx.contrast", "Contraste", 50, 150], ["saturation", "fx.saturation", "Saturation", 0, 200]];
const pill = "h-9 px-3 rounded-full text-xs font-semibold border border-white/20 inline-flex items-center gap-1.5 disabled:opacity-50";
const onStyle = { background: "var(--yalola-primary)", borderColor: "transparent" };
// Lot 536c — dernier niveau Beauté enregistré (mémoire propre à l'éditeur, distincte de la caméra) ; appliqué d'office
// aux prochaines photos importées, sauf sur un fichier déjà cuit avec un lissage (pas de double lissage).
const FX_BEAUTY_KEY = "yalola.fx_beauty";
const BEAUTY_BAKED = new WeakSet();
const readFxBeauty = () => { try { const v = parseInt(localStorage.getItem(FX_BEAUTY_KEY) || "0", 10); return v >= 0 && v <= 3 ? v : 0; } catch { return 0; } };
const writeFxBeauty = (v) => { try { localStorage.setItem(FX_BEAUTY_KEY, String(v)); } catch { /* stockage indisponible */ } };
const initialBeauty = (media) => (!media || media.kind === "video" || (media.file && BEAUTY_BAKED.has(media.file)) ? 0 : readFxBeauty());

export const MediaFxEditor = ({ open, media, initialFx, onApply, onClose, t }) => {
  const isVideo = media?.kind === "video";
  const [tab, setTab] = useState("filters");
  const [fx, setFx] = useState(() => normalizeFx(initialFx));
  const [geom, setGeom] = useState({ rotate: 0, flip: false }); // photo : pré-appliqué sur l'image de travail
  const [workSrc, setWorkSrc] = useState(media?.src || null); // photo tournée/miroir (blob) ; sinon source
  const [workFile, setWorkFile] = useState(null);
  const [crop, setCrop] = useState(null); // react-image-crop (unit %)
  const [aspectKey, setAspectKey] = useState("original");
  const [thumb, setThumb] = useState(null);
  const [busy, setBusy] = useState(false);
  const [peek, setPeek] = useState(false); // maintenir « Original » → aperçu sans filtre
  const [beauty, setBeauty] = useState(() => initialBeauty(media)); // photo : lissage visage 0..3
  const beautyTouched = useRef(false); // curseur modifié par l'utilisateur → mémorisé à l'enregistrement
  const [beautyState, setBeautyState] = useState("idle"); // idle | loading | ready | noface | error
  const [smoothSrc, setSmoothSrc] = useState(null); // aperçu lissé (blob) de workSrc
  const [workDims, setWorkDims] = useState(null); // dimensions naturelles de l'image de travail (cadre de recadrage)
  const imgRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => { if (open) { setTab("filters"); setFx(normalizeFx(initialFx)); setGeom({ rotate: 0, flip: false }); setWorkSrc(media?.src || null); setWorkFile(null); setCrop(null); setAspectKey("original"); setThumb(null); setPeek(false); setBeauty(initialBeauty(media)); beautyTouched.current = false; setBeautyState("idle"); setSmoothSrc((u) => { if (u) URL.revokeObjectURL(u); return null; }); } }, [open, media?.src]); // eslint-disable-line react-hooks/exhaustive-deps

  // Dimensions de l'image de travail (la photo affichée peut être l'aperçu lissé, réduit).
  useEffect(() => {
    if (!open || isVideo || !workSrc) { setWorkDims(null); return undefined; }
    let alive = true;
    const im = new Image(); im.onload = () => { if (alive) setWorkDims({ w: im.naturalWidth, h: im.naturalHeight }); }; im.src = workSrc;
    return () => { alive = false; };
  }, [open, isVideo, workSrc]);

  // Aperçu lissé recalculé (150 ms après le dernier changement) ; révoqué à chaque remplacement.
  useEffect(() => {
    if (!open || isVideo || !workSrc || !beauty) { setSmoothSrc((u) => { if (u) URL.revokeObjectURL(u); return null; }); if (!beauty) setBeautyState((s) => (s === "error" ? s : "idle")); return undefined; }
    let alive = true;
    setBeautyState("loading");
    const id = setTimeout(() => {
      smoothImageToUrl(workSrc, beauty)
        .then(({ url, faces }) => { if (!alive) { URL.revokeObjectURL(url); return; } setSmoothSrc((u) => { if (u) URL.revokeObjectURL(u); return url; }); setBeautyState(faces ? "ready" : "noface"); })
        .catch(() => { if (!alive) return; setBeautyState("error"); setBeauty(0); writeFxBeauty(0); toast.error(t("camera.beauty_error", "Lissage indisponible sur cet appareil.")); });
    }, 150);
    return () => { alive = false; clearTimeout(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isVideo, workSrc, beauty]);

  // Échap ferme l'éditeur seul (capture avant Radix : la boîte de dialogue du composeur reste ouverte).
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); onClose?.(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [open, onClose]);

  // Vignette commune des 10 filtres (photo : la source ; vidéo : une image capturée du lecteur, sinon dégradé).
  useEffect(() => {
    if (!open || !media?.src) return undefined;
    if (!isVideo) { setThumb(media.src); return undefined; }
    const v = videoRef.current;
    if (!v) return undefined;
    const grab = () => {
      try {
        const c = document.createElement("canvas"); c.width = 96; c.height = Math.max(1, Math.round(96 * (v.videoHeight || 16) / (v.videoWidth || 9)));
        c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
        setThumb(c.toDataURL("image/jpeg", 0.7));
      } catch { setThumb(null); }
    };
    if (v.readyState >= 2) grab(); else v.addEventListener("loadeddata", grab, { once: true });
    return () => v.removeEventListener("loadeddata", grab);
  }, [open, media?.src, isVideo]);

  // Photo : rotation / miroir pré-cuits dans une image de travail pour que le cadre de recadrage colle à l'image affichée.
  const sourceFile = async () => media.file || await fileFromUrl(media.src, "photo.jpg");
  const applyGeom = async (next) => {
    setBusy(true);
    try {
      const f = await bakeImageFx(await sourceFile(), { fx: { flip: next.flip }, rotate: next.rotate }, { quality: 0.95 });
      if (workSrc && workSrc !== media.src) URL.revokeObjectURL(workSrc);
      setWorkFile(f); setWorkSrc(URL.createObjectURL(f)); setGeom(next); setCrop(null);
    } finally { setBusy(false); }
  };

  const aspectValue = ASPECTS.find((a) => a.key === aspectKey)?.value ?? null;
  const onImgLoad = (e) => { if (aspectKey !== "original" && !crop) setCrop(initialCrop(e.currentTarget, aspectValue)); };
  const pickAspect = (key) => {
    setAspectKey(key);
    const el = imgRef.current;
    if (!el) return;
    const val = ASPECTS.find((a) => a.key === key)?.value ?? null;
    setCrop(key === "original" ? null : initialCrop(el, val));
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isVideo) { onApply({ fx: hasFx(fx) ? normalizeFx(fx) : null }); return; }
      if (beautyTouched.current) writeFxBeauty(beauty);
      const cropping = !!crop && aspectKey !== "original";
      const changed = hasFx(fx) || geom.rotate || geom.flip || cropping || beauty > 0;
      if (!changed) { onApply({}); return; }
      const base = workFile || await sourceFile();
      let px = null;
      const el = imgRef.current;
      const W = workDims?.w || el?.naturalWidth, H = workDims?.h || el?.naturalHeight;
      if (cropping && W && H) {
        px = { x: (crop.x / 100) * W, y: (crop.y / 100) * H, width: (crop.width / 100) * W, height: (crop.height / 100) * H };
      }
      const file = await bakeImageFx(base, { fx: { ...fx, flip: false }, crop: px, beauty });
      if (beauty > 0 || (media.file && BEAUTY_BAKED.has(media.file))) BEAUTY_BAKED.add(file);
      onApply({ file, fx: null });
    } finally { setBusy(false); }
  };

  if (!open || !media) return null;
  const shownFx = peek ? DEFAULT_FX : fx;
  const shownSrc = !peek && smoothSrc ? smoothSrc : workSrc;
  const previewStyle = fxStyle(shownFx);
  const tabs = isVideo ? TABS.filter((x) => x.key !== "crop") : TABS;
  const peekBtn = (hasFx(fx) || beauty > 0) && (
    <button type="button" onPointerDown={() => setPeek(true)} onPointerUp={() => setPeek(false)} onPointerLeave={() => setPeek(false)} onPointerCancel={() => setPeek(false)} className="absolute top-2 right-2 z-10 h-9 px-3 rounded-full bg-black/55 text-white text-xs font-semibold inline-flex items-center gap-1.5 select-none" data-testid="fx-peek" data-peek={peek ? 1 : 0}>
      <Eye size={14} /> {t("fx.original", "Original")}
    </button>
  );
  // Le composeur PC vit dans un Dialog Radix dont le FocusScope renvoie tout focus posé hors du Dialog → les curseurs
  // (Luminosité, Beauté…) ne répondaient pas au clavier. Un FocusScope propre met celui du Dialog en pause (pile Radix).
  return createPortal(
    <FocusScope asChild trapped loop>
    <div className="fixed inset-0 z-[95] bg-black text-white flex flex-col select-none" style={{ paddingTop: "var(--sa-top, 0px)", paddingBottom: "var(--sa-bottom, 0px)", pointerEvents: "auto" }} data-testid="fx-editor" data-kind={media.kind} data-tab={tab} data-beauty={isVideo ? undefined : beauty}>
      <div className="flex items-center justify-between px-3 h-14 shrink-0 w-full max-w-2xl mx-auto">
        <button type="button" onClick={onClose} className="h-10 px-3 rounded-full text-sm font-semibold hover:bg-white/10" data-testid="fx-cancel"><X size={18} className="inline mr-1 -mt-0.5" />{t("common.cancel", "Annuler")}</button>
        <div className="text-sm font-bold">{t(...(tabs.find((x) => x.key === tab)?.label || TABS[0].label))}</div>
        <button type="button" onClick={save} disabled={busy} className="h-10 px-4 rounded-full text-sm font-bold disabled:opacity-50" style={{ background: "var(--yalola-primary)" }} data-testid="fx-save">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <><Check size={16} className="inline mr-1 -mt-0.5" />{t("common.save", "Enregistrer")}</>}
        </button>
      </div>

      <div className="flex-1 min-h-0 relative grid place-items-center overflow-hidden px-2" data-testid="fx-preview">
        {isVideo ? (
          <video ref={videoRef} src={media.src} poster={media.poster || undefined} autoPlay muted loop playsInline crossOrigin="anonymous" className="max-h-full max-w-full object-contain" style={previewStyle} data-testid="fx-preview-video" />
        ) : tab === "crop" ? (
          <ReactCrop crop={crop || undefined} onChange={(_, pc) => setCrop(pc)} aspect={aspectValue || undefined} keepSelection className="max-h-full" data-testid="fx-crop">
            <img ref={imgRef} src={shownSrc} alt="" onLoad={onImgLoad} className="max-h-[60vh] max-w-full object-contain" style={{ filter: fxFilterCss(shownFx) }} draggable={false} data-testid="fx-crop-image" data-smoothed={smoothSrc && !peek ? 1 : 0} />
          </ReactCrop>
        ) : (
          <img ref={imgRef} src={shownSrc} alt="" className="max-h-full max-w-full object-contain" style={{ filter: fxFilterCss(shownFx) }} draggable={false} data-testid="fx-preview-image" data-smoothed={smoothSrc && !peek ? 1 : 0} />
        )}
        {peekBtn}
      </div>

      <div className="shrink-0 px-3 pt-3 pb-2 space-y-3 w-full max-w-2xl mx-auto" data-testid="fx-panel">
        {tab === "filters" && (
          <div className="flex gap-3 overflow-x-auto hide-scrollbar pb-1 -mx-1 px-1" data-testid="fx-presets">
            {FX_PRESETS.map((p) => {
              const on = fx.preset === p.key;
              return (
                <button key={p.key} type="button" onClick={() => setFx((f) => ({ ...f, preset: p.key }))} className="shrink-0 flex flex-col items-center gap-1.5 w-[64px]" data-testid={`fx-preset-${p.key}`} data-on={on ? 1 : 0}>
                  <span className="h-16 w-16 rounded-2xl overflow-hidden border-2" style={{ borderColor: on ? "var(--yalola-primary)" : "transparent", background: GRADIENT_THUMB }}>
                    {thumb && <img src={thumb} alt="" className="h-full w-full object-cover" style={{ filter: fxFilterCss({ ...DEFAULT_FX, preset: p.key }) }} draggable={false} />}
                  </span>
                  <span className={`text-[11px] leading-none truncate w-full text-center ${on ? "font-bold" : "text-white/70"}`}>{t(`fx.preset_${p.key}`, p.label)}</span>
                </button>
              );
            })}
          </div>
        )}
        {tab === "adjust" && (
          <div className="space-y-3" data-testid="fx-adjust">
            {ADJUSTS.map(([k, key, label, min, max]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="text-xs w-24 shrink-0 text-white/80">{t(key, label)}</span>
                <Slider value={[fx[k]]} min={min} max={max} step={1} onValueChange={([v]) => setFx((f) => ({ ...f, [k]: v }))} className="flex-1" data-testid={`fx-slider-${k}`} />
                <span className="text-xs w-9 text-right tabular-nums text-white/70" data-testid={`fx-value-${k}`}>{fx[k]}</span>
              </div>
            ))}
            {!isVideo && (
              <div className="flex items-center gap-3" data-testid="fx-beauty-row" data-state={beautyState}>
                <span className="text-xs w-24 shrink-0 text-white/80 inline-flex items-center gap-1.5"><Sparkles size={13} /> {t("camera.beauty", "Beauté")}</span>
                <Slider value={[beauty]} min={0} max={3} step={1} onValueChange={([v]) => { beautyTouched.current = true; setBeauty(v); }} className="flex-1" data-testid="fx-slider-beauty" />
                <span className="text-xs w-14 text-right text-white/70 inline-flex items-center justify-end gap-1" data-testid="fx-value-beauty" data-level={beauty}>
                  {beautyState === "loading" ? <Loader2 size={12} className="animate-spin" /> : t(`camera.beauty_${BEAUTY_LEVELS[beauty].key}`, ["Sans", "Léger", "Moyen", "Fort"][beauty])}
                </span>
              </div>
            )}
            {!isVideo && beauty > 0 && beautyState === "noface" && (
              <div className="text-[11px] text-white/60 pl-[6.75rem]" data-testid="fx-beauty-noface">{t("camera.beauty_noface", "Aucun visage détecté")}</div>
            )}
            <div className="flex items-center gap-2 pt-1">
              {isVideo && (
                <button type="button" onClick={() => setFx((f) => ({ ...f, flip: !f.flip }))} className={pill} style={fx.flip ? onStyle : {}} data-testid="fx-flip"><FlipHorizontal2 size={14} /> {t("fx.mirror", "Miroir")}</button>
              )}
              <button type="button" onClick={() => { setFx((f) => ({ ...f, brightness: 100, contrast: 100, saturation: 100 })); if (beauty) beautyTouched.current = true; setBeauty(0); }} className={pill} data-testid="fx-reset">{t("fx.reset", "Réinitialiser")}</button>
            </div>
          </div>
        )}
        {tab === "crop" && !isVideo && (
          <div className="space-y-3" data-testid="fx-crop-panel">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar -mx-1 px-1">
              {ASPECTS.map((a) => (
                <button key={a.key} type="button" onClick={() => pickAspect(a.key)} className="shrink-0 h-9 px-3.5 rounded-full text-xs font-semibold border border-white/20" style={aspectKey === a.key ? onStyle : {}} data-testid={`fx-aspect-${a.key}`}>{t(`fx.aspect_${a.key}`, a.label)}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" disabled={busy} onClick={() => applyGeom({ ...geom, rotate: (geom.rotate + 90) % 360 })} className={pill} data-testid="fx-rotate"><RotateCw size={14} /> {t("fx.rotate", "Pivoter")}</button>
              <button type="button" disabled={busy} onClick={() => applyGeom({ ...geom, flip: !geom.flip })} className={pill} style={geom.flip ? onStyle : {}} data-testid="fx-flip"><FlipHorizontal2 size={14} /> {t("fx.mirror", "Miroir")}</button>
              {aspectKey === "free" && <span className="text-[11px] text-white/60">{t("fx.free_hint", "Tire les coins du cadre.")}</span>}
            </div>
          </div>
        )}
        <div className="flex items-center justify-around pt-1 border-t border-white/10" role="tablist">
          {tabs.map(({ key, Icon, label }) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)} className={`relative flex flex-col items-center gap-1 py-2 px-4 text-[11px] ${tab === key ? "font-bold" : "text-white/60"}`} style={tab === key ? { color: "var(--yalola-primary)" } : {}} data-testid={`fx-tab-${key}`}>
              <Icon size={20} /> {t(...label)}
              {key === "adjust" && !isVideo && beauty > 0 && <span className="absolute top-1.5 right-2.5 h-1.5 w-1.5 rounded-full" style={{ background: "var(--yalola-primary)" }} data-testid="fx-beauty-dot" />}
            </button>
          ))}
        </div>
      </div>
    </div>
    </FocusScope>,
    document.body
  );
};

// Cadre initial centré : ratio imposé → makeAspectCrop ; forme libre → 80 % de l'image.
const initialCrop = (el, aspect) => {
  const w = el.width, h = el.height;
  if (!w || !h) return null;
  if (aspect) return centerCrop(makeAspectCrop({ unit: "%", width: 90 }, aspect, w, h), w, h);
  return { unit: "%", x: 10, y: 10, width: 80, height: 80 };
};

// Ouverture « promesse » : const res = await openFx(media, fx) → { file } (photo cuite) | { fx } (vidéo) | {} | null (annulé).
export const useFxEditor = () => {
  const [state, setState] = useState(null); // { media, initialFx, resolve }
  const openFx = (media, initialFx) => new Promise((resolve) => setState({ media, initialFx, resolve }));
  const props = useMemo(() => ({
    open: !!state, media: state?.media, initialFx: state?.initialFx,
    onApply: (res) => { state?.resolve(res); setState(null); },
    onClose: () => { state?.resolve(null); setState(null); },
  }), [state]);
  return { openFx, fxProps: props };
};

// Radix Dialog : un clic dans l'éditeur (portail hors du contenu) ne doit pas fermer la boîte du composeur.
export const keepDialogOpenForFx = (e) => { if (e?.target?.closest?.('[data-testid="fx-editor"]')) e.preventDefault(); };

// Bouton « Filtres » posé sur un aperçu média (composeur de publication).
export const FxButton = ({ onClick, active = false, className = "", testId, t }) => (
  <button type="button" onClick={onClick} className={`z-20 inline-flex items-center gap-1 rounded-full bg-black/60 text-white px-2.5 h-7 text-xs font-semibold hover:bg-black/80 ${className}`} style={active ? { background: "var(--yalola-primary)" } : {}} data-testid={testId} data-active={active ? 1 : 0}>
    <Sparkles size={13} /> {t("fx.button", "Filtres")}
  </button>
);

export default MediaFxEditor;
